import { useRef, useState, useEffect } from "react";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { PRODUCTS } from "@/data/site";

/* ─── tiny accent colours per product ─────────────────────────────────────── */
const ACCENT = {
  tripywiz: { dot: "bg-sky-400",   badge: "text-sky-600  bg-sky-50  border-sky-200" },
  aidni:    { dot: "bg-violet-400", badge: "text-violet-600 bg-violet-50 border-violet-200" },
  kadai:    { dot: "bg-emerald-400",badge: "text-emerald-600 bg-emerald-50 border-emerald-200" },
};

/* ─── mobile carousel ──────────────────────────────────────────────────────── */
function MobileCarousel() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[i];
    if (card) {
      track.scrollTo({ left: card.offsetLeft - 16, behavior: "smooth" });
      setActive(i);
    }
  };

  /* sync dot indicator while dragging */
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const scrollCenter = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    Array.from(track.children).forEach((child, i) => {
      if (Math.abs(child.offsetLeft + child.offsetWidth / 2 - scrollCenter) <
          Math.abs(track.children[closest].offsetLeft + track.children[closest].offsetWidth / 2 - scrollCenter)) {
        closest = i;
      }
    });
    setActive(closest);
  };

  return (
    <div className="md:hidden relative">
      {/* track */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-4 -mx-4 px-4"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {PRODUCTS.map((p) => {
          const ac = ACCENT[p.id] ?? ACCENT.tripywiz;
          return (
            <article
              key={p.id}
              data-testid={`product-${p.id}-mobile`}
              className="snap-center shrink-0 w-[85vw] max-w-[340px] flex flex-col rounded-2xl border border-zinc-200 bg-white overflow-hidden shadow-sm"
            >
              {/* image */}
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="relative block aspect-[16/10] bg-zinc-50 overflow-hidden"
              >
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-zinc-900/90 backdrop-blur-md px-2.5 py-1 rounded-full font-mono-tech text-[9px] tracking-[0.2em] uppercase text-white">
                  <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${ac.dot}`} />
                  Live
                </span>
              </a>

              {/* body */}
              <div className="flex flex-col flex-1 p-5">
                <span className={`self-start text-[10px] font-mono-tech tracking-widest uppercase border px-2 py-0.5 rounded-full ${ac.badge}`}>
                  {p.category}
                </span>
                <h3 className="mt-2 font-display text-2xl text-zinc-900 tracking-tight">{p.name}</h3>
                <p className="mt-1 text-zinc-600 text-sm leading-relaxed">{p.tagline}</p>

                <ul className="mt-4 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-zinc-600">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-zinc-400" />
                      {f}
                    </li>
                  ))}
                </ul>

                {p.stats && p.stats.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 divide-x divide-zinc-100 border border-zinc-100 rounded-xl overflow-hidden bg-zinc-50">
                    {p.stats.map((s) => (
                      <div key={s.label} className="px-2 py-3 text-center">
                        <p className="font-mono-tech text-sm font-bold text-zinc-900">{s.value}</p>
                        <p className="font-mono-tech text-[9px] text-zinc-400 tracking-widest uppercase mt-0.5 leading-tight">{s.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center justify-center gap-1.5 bg-zinc-900 text-white rounded-xl px-5 py-3 text-sm font-medium"
                >
                  Launch {p.name} <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </article>
          );
        })}
      </div>

      {/* dot indicators */}
      <div className="flex items-center justify-center gap-2 mt-2">
        {PRODUCTS.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to product ${i + 1}`}
            onClick={() => scrollTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${i === active ? "bg-zinc-900 w-5" : "bg-zinc-300"}`}
          />
        ))}
      </div>

      {/* prev / next arrows */}
      <div className="flex items-center justify-center gap-3 mt-4">
        <button
          aria-label="Previous product"
          onClick={() => scrollTo(Math.max(0, active - 1))}
          disabled={active === 0}
          className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-500 hover:border-zinc-400 hover:text-zinc-900 disabled:opacity-30 transition-all"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          aria-label="Next product"
          onClick={() => scrollTo(Math.min(PRODUCTS.length - 1, active + 1))}
          disabled={active === PRODUCTS.length - 1}
          className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-500 hover:border-zinc-400 hover:text-zinc-900 disabled:opacity-30 transition-all"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ─── desktop stack ────────────────────────────────────────────────────────── */
function DesktopStack() {
  return (
    <div className="hidden md:flex flex-col gap-12">
      {PRODUCTS.map((p, idx) => {
        const reverse = idx % 2 === 1;
        const ac = ACCENT[p.id] ?? ACCENT.tripywiz;
        return (
          <Reveal key={p.id} delay={idx * 0.06}>
            <article
              data-testid={`product-${p.id}`}
              className="group relative border border-zinc-200/80 bg-white/80 backdrop-blur-md overflow-hidden hover:border-zinc-400 hover:shadow-2xl hover:shadow-zinc-200/60 transition-all duration-500 rounded-2xl"
            >
              <div className="grid grid-cols-12 items-stretch">
                {/* image side */}
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`col-span-7 relative block bg-zinc-50 overflow-hidden min-h-[340px] ${reverse ? "order-2 border-l border-zinc-100/80" : "border-r border-zinc-100/80"}`}
                >
                  <img
                    src={p.image}
                    alt={`${p.name} live preview`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent pointer-events-none" />
                  <span className="absolute top-5 left-5 inline-flex items-center gap-2 bg-zinc-900/90 backdrop-blur-md px-3.5 py-1.5 rounded-full font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white shadow-lg">
                    <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${ac.dot}`} />
                    Live Product
                  </span>
                </a>

                {/* content side */}
                <div className={`col-span-5 p-10 xl:p-14 flex flex-col justify-between ${reverse ? "order-1" : ""}`}>
                  <div>
                    <span className={`inline-block text-[10px] font-mono-tech tracking-widest uppercase border px-2.5 py-0.5 rounded-full ${ac.badge}`}>
                      {p.category}
                    </span>
                    <h3 className="mt-4 font-display text-4xl text-zinc-900 tracking-tighter">
                      {p.name}
                    </h3>
                    <p className="mt-3 text-zinc-700 font-medium text-[15px] leading-relaxed">{p.tagline}</p>
                    <p className="mt-2 text-zinc-500 text-sm leading-relaxed">{p.description}</p>

                    <ul className="mt-6 space-y-3">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm text-zinc-700">
                          <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-zinc-100 shrink-0">
                            <Check className="w-3 h-3 stroke-[2.5]" />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-10 pt-8 border-t border-zinc-100">
                    {p.stats && p.stats.length > 0 && (
                      <div className={`grid grid-cols-${p.stats.length} gap-px bg-zinc-100 border border-zinc-100 rounded-xl overflow-hidden mb-6`}>
                        {p.stats.map((s) => (
                          <div key={s.label} className="bg-white/90 p-4 text-center">
                            <p className="font-mono-tech text-lg font-bold text-zinc-900 tracking-tight">{s.value}</p>
                            <p className="font-mono-tech text-[9px] text-zinc-400 tracking-[0.15em] uppercase mt-1 leading-tight">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`product-${p.id}-cta`}
                      className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-700 text-white rounded-xl px-6 py-4 text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-lg"
                    >
                      Launch {p.name}
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </Reveal>
        );
      })}
    </div>
  );
}

/* ─── section ──────────────────────────────────────────────────────────────── */
export default function ProductsSection({ index = "/04" }) {
  return (
    <section id="products" data-testid="products-section" className="py-20 md:py-32 border-t border-zinc-200 bg-zinc-50/30">
      <div className="container-tight">
        {/* header */}
        <div className="grid md:grid-cols-12 gap-6 items-end mb-12 md:mb-20">
          <Reveal className="md:col-span-7">
            <SectionLabel index={index} testid="home-products-label">Our SaaS products</SectionLabel>
            <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl text-zinc-900 tracking-tighter leading-[1.02]">
              We don&apos;t just build for clients.
              <br />
              <span className="bg-gradient-to-r from-zinc-900 to-zinc-500 bg-clip-text text-transparent">
                We build our own platforms.
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <p className="text-zinc-600 leading-relaxed md:text-right text-[15px]">
              Three active SaaS products in production — driving workflows in travel, AI support, and conversational commerce.
            </p>
          </Reveal>
        </div>

        {/* mobile carousel / desktop stack */}
        <Reveal>
          <MobileCarousel />
        </Reveal>
        <DesktopStack />
      </div>
    </section>
  );
}
